import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { PasswordInput } from '../components/PasswordInput';
import { PasswordMatchValidator } from '../components/PasswordMatchValidator';

/**
 * Pantalla pública /auth/reset-password?token=XXX (v1.3.1).
 *
 * Se monta FUERA de AuthProvider (ver PublicAuthRouter). Lee el `token` del
 * query string, pide nueva contraseña + confirmación, valida (match + política
 * mínima del backend: ≥8, una mayúscula, un número) y llama al endpoint. Éxito →
 * pantalla de confirmación con link a login. Token inválido/expirado → mensaje.
 */
export function ResetPasswordScreen() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get('token') ?? '',
    [],
  );

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Misma política que el backend (ResetPasswordDto / ChangePasswordDto).
  const policyError = (pwd: string): string | null => {
    if (pwd.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
    if (!/[A-Z]/.test(pwd)) return 'Incluye al menos una letra mayúscula.';
    if (!/[0-9]/.test(pwd)) return 'Incluye al menos un número.';
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    const pe = policyError(newPassword);
    if (pe) {
      setError(pe);
      return;
    }

    setSubmitting(true);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: { token, newPassword },
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          'El enlace es inválido o expiró. Solicita uno nuevo desde "¿Olvidaste tu contraseña?".',
        );
      } else {
        setError('No se pudo actualizar la contraseña. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            FluidSol
          </p>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">
            Nueva contraseña
          </h1>
        </div>

        {done ? (
          <div className="space-y-4">
            <p className="rounded-xl bg-emerald-50 px-3 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
              Tu contraseña fue actualizada. Inicia sesión con tu nueva
              contraseña.
            </p>
            <a
              href="/"
              className="block w-full rounded-xl bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Iniciar sesión
            </a>
          </div>
        ) : !token ? (
          <div className="space-y-4">
            <p className="alert-over rounded-xl px-3 py-2 text-xs">
              Enlace inválido: falta el token. Solicita un nuevo enlace de
              restablecimiento.
            </p>
            <a
              href="/auth/forgot-password"
              className="block w-full rounded-xl bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Solicitar nuevo enlace
            </a>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Nueva contraseña
              </span>
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                required
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                Confirmar contraseña
              </span>
              <PasswordInput
                value={confirm}
                onChange={setConfirm}
                autoComplete="new-password"
                required
              />
              <PasswordMatchValidator password={newPassword} confirmPassword={confirm} />
            </label>

            <p className="text-[11px] text-slate-400">
              Mínimo 8 caracteres, una mayúscula y un número.
            </p>

            {error && (
              <p className="alert-over rounded-xl px-3 py-2 text-xs">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {submitting ? 'Guardando…' : 'Establecer nueva contraseña'}
            </button>

            <a
              href="/"
              className="block text-center text-xs font-medium text-slate-500 underline hover:text-slate-800"
            >
              Volver al inicio de sesión
            </a>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordScreen;
